import {
  Checkbox,
  Chip,
  FormGroup,
  FormHelperText,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  useTheme,
} from '@mui/material';
import { Field, FieldArray, useFormikContext } from 'formik';
import { camelCase, find, findIndex, get } from 'lodash';
import React, { useMemo } from 'react';

import { InputComponent } from '..';
import { DividerWithText } from '../../../components';

export type InputChecklistOption = {
  value: string;
  label: string;
  disabled?: boolean;
  sortValue?: number;
  isActive?: boolean;
  isEditable?: boolean;
  isDeletable?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  tag?: React.ReactNode; // Optional tag for additional categorization
  description?: string; // Optional description for the option
};

export interface InputChecklistProps extends InputComponent {
  label?: string;
  className?: string;
  size?: 'sm';
  options?: InputChecklistOption[];
  gap?: number;
  action?: (option: InputChecklistOption) => void;
}

export function InputChecklist({
  disabled,
  hidden,
  label,
  readOnly,
  required,
  nameOverride,
  action,
  hint,
  options = [],
}: InputChecklistProps) {
  const { errors, setFieldTouched, touched, values } =
    useFormikContext<Record<string, any>>();
  const theme = useTheme();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  const fieldValues = get(values, name) ?? [];
  const isFieldTouched = get(touched, name) ?? false;

  const errorText = useMemo(() => {
    if (error) {
      return error as string;
    }
    if (required && isFieldTouched && fieldValues.length === 0) {
      return `${label} is required.`;
    }
    return null;
  }, [error, fieldValues.length, isFieldTouched, label, required]);

  const isSelected = (option: InputChecklistOption) => {
    const found = find(fieldValues, { value: option.value });
    return Boolean(found || option.isActive);
  };

  return (
    <FormGroup>
      {label && (
        <DividerWithText
          text={`${label}${required ? '*' : ''}`}
          align="left"
          slotProps={{
            text: {
              mx: 1,
              sx: { color: errorText ? 'error.main' : 'text.secondary' },
            },
          }}
        />
      )}
      <List disablePadding dense sx={{ width: 1 }}>
        <FieldArray
          name={name}
          render={({ remove, push }) =>
            options?.map((option, idx) => (
              <Field
                key={idx}
                id={`form.${name}.${option.value}`}
                as={ListItemButton}
                sx={{
                  '&.Mui-disabled': {
                    opacity: 0.55,
                  },
                }}
                disabled={option.disabled || disabled}
                onClick={() => {
                  if (readOnly || disabled || option.disabled) return;
                  setFieldTouched(name, true);

                  if (action) {
                    return action(option);
                  }

                  const foundIdx = findIndex(fieldValues, {
                    value: option.value,
                  });
                  if (foundIdx > -1) {
                    remove(foundIdx);
                  } else {
                    push(option);
                  }
                }}
              >
                <ListItem
                  key={idx}
                  disableGutters
                  sx={{ py: 0 }}
                  secondaryAction={
                    !!option.tag && (
                      <Chip
                        label={option.tag}
                        size="small"
                        sx={{ ml: 0.5, fontSize: '0.65rem' }}
                      />
                    )
                  }
                >
                  <ListItemAvatar>
                    <Checkbox
                      checked={isSelected(option)}
                      readOnly={readOnly}
                      disabled={option.disabled || disabled}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={option.label}
                    secondary={option.description}
                    slotProps={{
                      secondary: {
                        sx: { fontSize: '0.75rem', color: 'inherit' },
                      },
                    }}
                  />
                </ListItem>
              </Field>
            ))
          }
        />
      </List>
      {(!!errorText || !!hint) && (
        <FormHelperText error={!!errorText}>{errorText || hint}</FormHelperText>
      )}
    </FormGroup>
  );
}
